const memo = (getDeps, fn, opts) => {
  let deps = []
  let result = undefined

  return depArgs => {
    let depTime
    if (opts.key && opts.debug) depTime = Date.now()

    const newDeps = getDeps(depArgs)

    const depsChanged =
      newDeps.length !== deps.length ||
      newDeps.some((dep, index) => deps[index] !== dep)

    if (!depsChanged) {
      return result
    }

    deps = newDeps

    let resultTime
    if (opts.key && opts.debug) resultTime = Date.now()

    result = fn(...newDeps)
    opts?.onChange?.(result)

    if (opts.key && opts.debug) {
      if (opts?.debug()) {
        const depEndTime = Math.round((Date.now() - depTime) * 100) / 100
        const resultEndTime = Math.round((Date.now() - resultTime) * 100) / 100
        const resultFpsPercentage = resultEndTime / 16

        const pad = (str, num) => {
          str = String(str)
          while (str.length < num) {
            str = " " + str
          }
          return str
        }

        console.info(
          `%c⏱ ${pad(resultEndTime, 5)} /${pad(depEndTime, 5)} ms`,
          `
              font-size: .6rem;
              font-weight: bold;
              color: hsl(${Math.max(
                0,
                Math.min(120 - 120 * resultFpsPercentage, 120)
              )}deg 100% 31%);`,
          opts?.key
        )
      }
    }

    return result
  }
}

const getMemoOptions = (tableOptions, debugLevel, key, onChange) => {
  return {
    debug: () => tableOptions?.debugAll ?? tableOptions[debugLevel],
    key: process.env.REACT_APP_ENV === "dev" && key,
    onChange,
  }
}

export const customRowModel = () => {
  return table =>
    memo(
      () => [table.getState().sorting, table.getPreSortedRowModel()],
      (sorting, rowModel) => {
        if (!rowModel.rows.length || !sorting?.length) {
          return rowModel
        }

        const sortingState = table.getState().sorting

        const sortedFlatRows = []

        // Filter out sortings that correspond to non existing columns
        const availableSorting = sortingState.filter(sort =>
          table.getColumn(sort.id)?.getCanSort()
        )

        const columnInfoById = {}

        availableSorting.forEach(sortEntry => {
          const column = table.getColumn(sortEntry.id)
          if (!column) return

          columnInfoById[sortEntry.id] = {
            sortUndefined: column.columnDef.sortUndefined,
            invertSorting: column.columnDef.invertSorting,
            sortingFn: column.getSortingFn(),
          }
        })

        const sortData = rows => {
          // This will also perform a stable sorting using the row index
          // if needed.
          const sortedData = rows.map(row => ({ ...row }))

          sortedData.sort((rowA, rowB) => {
            for (let i = 0; i < availableSorting.length; i += 1) {
              const sortEntry = availableSorting[i]
              const columnInfo = columnInfoById[sortEntry.id]
              const isDesc = sortEntry?.desc ?? false

              let sortInt = 0

              // All sorting ints should always return in ascending order
              if (columnInfo.sortUndefined) {
                const aValue = rowA.getValue(sortEntry.id)
                const bValue = rowB.getValue(sortEntry.id)

                const aUndefined = aValue === undefined
                const bUndefined = bValue === undefined

                if (aUndefined || bUndefined) {
                  sortInt =
                    aUndefined && bUndefined
                      ? 0
                      : aUndefined
                      ? columnInfo.sortUndefined
                      : -columnInfo.sortUndefined
                }
              }

              if (sortInt === 0) {
                sortInt = columnInfo.sortingFn(rowA, rowB, sortEntry.id)
              }

              // If sorting is non-zero, take care of desc and inversion
              if (sortInt !== 0) {
                if (isDesc) {
                  sortInt *= -1
                }

                if (columnInfo.invertSorting) {
                  sortInt *= -1
                }

                return sortInt
              }
            }

            return rowA.index - rowB.index
          })

          return sortedData
        }

        return {
          rows: rowModel?.rows?.map(row => ({
            ...row,
            subRows: sortData(row?.subRows),
          })),
          flatRows: sortedFlatRows,
          rowsById: rowModel.rowsById,
        }
      },
      getMemoOptions(table.options, "debugTable", "getSortedRowModel", () =>
        table._autoResetPageIndex()
      )
    )
}
